/**
 * kordAI 4성부 화성 풀이 — 소프라노 또는 베이스 한 성부만 주어졌을 때 나머지 3성을 채운다.
 *
 * 접근: 규칙 기반 휴리스틱(그리디, 백트래킹 없음 — v1).
 * 매 박(beat)마다 주어진 성부의 음이 속할 수 있는 온음계 3화음 후보들을 찾고,
 * 각 후보마다 나머지 성부들의 음역 안에서 가능한 보이싱을 만든 다음,
 * analyzeHarmonyPayload()로 직전 박과 이어붙여 실제 성부진행 위반 개수를 세어
 * 가장 위반이 적은(동점이면 진행이 가장 매끄러운) 보이싱을 채택한다.
 *
 * 현재 장조 12키만 지원(Toolbar의 KEY_SIGNATURES와 동일) — 온음계 3화음(I~vii°)만 사용,
 * 2차딸림화음 등 반음계적 화음은 다루지 않음.
 */
import { BEAT_VALUES, VOICE_NAMES, getBeatsPerMeasure, getVoiceRanges, groupIntoMeasures, pitchToMidi } from './musicTheory.js'
import { analyzeHarmonyPayload } from './harmonyEngine.js'

const KEY_TO_TONIC_PC = { C: 0, G: 7, D: 2, A: 9, E: 4, B: 11, 'F#': 6, F: 5, Bb: 10, Eb: 3, Ab: 8, Db: 1 }

// 각 장조의 온음계 7음 스펠링(옥타브 없이) — 근음/3음/5음 스펠링에 사용
const KEY_SCALE_SPELLING = {
  C: ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
  G: ['G', 'A', 'B', 'C', 'D', 'E', 'F#'],
  D: ['D', 'E', 'F#', 'G', 'A', 'B', 'C#'],
  A: ['A', 'B', 'C#', 'D', 'E', 'F#', 'G#'],
  E: ['E', 'F#', 'G#', 'A', 'B', 'C#', 'D#'],
  B: ['B', 'C#', 'D#', 'E', 'F#', 'G#', 'A#'],
  'F#': ['F#', 'G#', 'A#', 'B', 'C#', 'D#', 'E#'],
  F: ['F', 'G', 'A', 'Bb', 'C', 'D', 'E'],
  Bb: ['Bb', 'C', 'D', 'Eb', 'F', 'G', 'A'],
  Eb: ['Eb', 'F', 'G', 'Ab', 'Bb', 'C', 'D'],
  Ab: ['Ab', 'Bb', 'C', 'Db', 'Eb', 'F', 'G'],
  Db: ['Db', 'Eb', 'F', 'Gb', 'Ab', 'Bb', 'C'],
}

const MAJOR_STEPS = [0, 2, 4, 5, 7, 9, 11] // 도수별 근음 대비 반음 오프셋
const MAJOR_DEGREE_QUALITY = ['maj', 'min', 'min', 'maj', 'maj', 'min', 'dim']

/** 성부별 음역 안에서 주어진 음이름(letterAcc, 옥타브 없음)이 될 수 있는 모든 (pitch, midi) 후보. */
function pitchOptionsForRole(letterAcc, range) {
  const out = []
  for (let oct = 0; oct <= 8; oct++) {
    const pitch = `${letterAcc}${oct}`
    const midi = pitchToMidi(pitch)
    if (midi == null) continue
    if (midi >= range.minMidi && midi <= range.maxMidi) out.push({ pitch, midi })
  }
  return out
}

/** 도수(0~6)의 근음·3음·5음 스펠링+피치클래스. */
function degreeChordTones(key, degree) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  const roleDegrees = { root: degree, third: (degree + 2) % 7, fifth: (degree + 4) % 7 }
  const tones = {}
  for (const [role, d] of Object.entries(roleDegrees)) {
    tones[role] = {
      letterAcc: spelling[d],
      pc: (tonicPc + MAJOR_STEPS[d]) % 12,
    }
  }
  return { quality: MAJOR_DEGREE_QUALITY[degree], tones }
}

/** 주어진 성부의 피치클래스가 근음/3음/5음이 될 수 있는 온음계 3화음 후보들(도수 0~6 중). */
function candidateChordsForPc(key, pc) {
  const out = []
  for (let degree = 0; degree < 7; degree++) {
    const { quality, tones } = degreeChordTones(key, degree)
    for (const role of ['root', 'third', 'fifth']) {
      if (tones[role].pc === pc) {
        out.push({ degree, quality, tones, givenRole: role })
        break
      }
    }
  }
  return out
}

/** 도블링(중복) 우선순위(근음>5음>3음)에 따라, 주어진 성부의 역할을 뺀 나머지 3개 성부의 역할 배열. */
function rolesForMissingVoices(givenRole) {
  const schemes = [
    ['root', 'root', 'third', 'fifth'],
    ['root', 'fifth', 'fifth', 'third'],
    ['root', 'third', 'third', 'fifth'],
  ]
  const out = []
  for (const scheme of schemes) {
    const remaining = [...scheme]
    const idx = remaining.indexOf(givenRole)
    if (idx === -1) continue
    remaining.splice(idx, 1)
    out.push(remaining)
  }
  return out
}

const UPPER_ADJACENT_PAIRS = [['soprano', 'alto'], ['alto', 'tenor']]

/** missingVoices(순서: VOICE_NAMES 순) 각각에 role별 pitch 후보를 배정해 유효한 조합을 모두 만든다. */
function enumerateVoicings(missingVoices, roles, tones, ranges, givenVoice, givenPitchStr, givenMidi) {
  // missingVoices와 roles는 같은 길이(3) — role[i]가 missingVoices[i]의 화음음 역할
  const optionsPerVoice = missingVoices.map((v, i) => pitchOptionsForRole(tones[roles[i]].letterAcc, ranges[v]))
  if (optionsPerVoice.some((opts) => opts.length === 0)) return []

  const combos = []
  const n = missingVoices.length
  const idxs = new Array(n).fill(0)
  function backtrack(pos) {
    if (pos === n) {
      const midis = { [givenVoice]: givenMidi }
      const pitches = { [givenVoice]: givenPitchStr }
      for (let i = 0; i < n; i++) {
        const opt = optionsPerVoice[i][idxs[i]]
        midis[missingVoices[i]] = opt.midi
        pitches[missingVoices[i]] = opt.pitch
      }
      if (isOrderedAndSpaced(midis)) {
        combos.push({ midis, pitches })
      }
      return
    }
    for (let k = 0; k < optionsPerVoice[pos].length; k++) {
      idxs[pos] = k
      backtrack(pos + 1)
    }
  }
  backtrack(0)
  return combos
}

function isOrderedAndSpaced(full) {
  const { soprano, alto, tenor, bass } = full
  if (!(soprano >= alto && alto >= tenor && tenor >= bass)) return false
  for (const [a, b] of UPPER_ADJACENT_PAIRS) {
    if (full[a] - full[b] > 12) return false
  }
  return true
}

/** 후보 보이싱을 직전 슬롯과 이어붙여 analyzeHarmonyPayload로 위반 개수 + 이동량을 채점. */
function scoreCandidate(key, timeSignature, theoryBase, prevPitches, candMidis, candPitches) {
  const measures = []
  if (prevPitches) {
    measures.push({
      measure: 1,
      soprano: [`${prevPitches.soprano}:q`],
      alto: [`${prevPitches.alto}:q`],
      tenor: [`${prevPitches.tenor}:q`],
      bass: [`${prevPitches.bass}:q`],
    })
  }
  measures.push({
    measure: measures.length + 1,
    soprano: [`${candPitches.soprano}:q`],
    alto: [`${candPitches.alto}:q`],
    tenor: [`${candPitches.tenor}:q`],
    bass: [`${candPitches.bass}:q`],
  })
  // 마디당 1박(각 마디=음표 하나)으로 선언 — 4/4로 두면 남는 빈 박이 prevMidis를 덮어써 버려서
  // 직전 화음 정보가 유실되고 병행5·8도 등 성부진행 검사가 무력화되므로 반드시 이렇게 해야 함.
  const payload = { key, timeSignature: '1/4', theoryBase, measures }
  let issues = 0
  try {
    const result = analyzeHarmonyPayload(payload)
    const lastMeasure = result.measures[result.measures.length - 1]
    issues = (lastMeasure?.beats?.[0]?.voiceLeadingIssues || lastMeasure?.voiceLeadingIssues || []).length
  } catch {
    issues = 99
  }

  let motion = 0
  if (prevPitches) {
    for (const v of VOICE_NAMES) {
      const prevMidi = pitchToMidi(prevPitches[v])
      if (prevMidi != null) motion += Math.abs(candMidis[v] - prevMidi)
    }
  }
  return { issues, motion }
}

/**
 * @param {object} opts
 * @param {'C'|'G'|'D'|'A'|'E'|'B'|'F#'|'F'|'Bb'|'Eb'|'Ab'|'Db'} opts.key
 * @param {string} opts.timeSignature
 * @param {{soprano:Array,alto:Array,tenor:Array,bass:Array}} opts.score - score[givenVoice]만 채워져 있으면 됨
 * @param {'soprano'|'bass'} opts.givenVoice
 * @param {'baek'|'kostka'} [opts.theoryBase]
 * @returns {{score: object, warnings: string[]}}
 */
export function solveFourPartHarmony({ key, timeSignature, score, givenVoice, theoryBase = 'baek' }) {
  if (givenVoice !== 'soprano' && givenVoice !== 'bass') {
    throw new Error("givenVoice는 'soprano' 또는 'bass'여야 합니다.")
  }
  const beatsPerMeasure = getBeatsPerMeasure(timeSignature)
  const ranges = getVoiceRanges(theoryBase)
  const givenNotes = score[givenVoice] || []
  const measures = groupIntoMeasures(givenNotes, beatsPerMeasure)

  const slots = []
  for (const m of measures) {
    for (let b = 0; b < beatsPerMeasure; b++) {
      const t = b + 0.5
      let acc = 0
      let found = null
      for (const n of m.notes) {
        const dur = BEAT_VALUES[n.duration] ?? 1
        if (t >= acc && t < acc + dur) { found = n; break }
        acc += dur
      }
      slots.push(found && !found.isRest ? found.pitch : null)
    }
  }

  const missingVoices = VOICE_NAMES.filter((v) => v !== givenVoice)
  const filled = { soprano: [], alto: [], tenor: [], bass: [] }
  const warnings = []
  let prevPitches = null

  for (let i = 0; i < slots.length; i++) {
    const givenPitchStr = slots[i]
    if (!givenPitchStr) {
      for (const v of VOICE_NAMES) filled[v].push({ isRest: true, duration: 'q' })
      prevPitches = null
      continue
    }
    const givenMidi = pitchToMidi(givenPitchStr)
    const givenPc = ((givenMidi % 12) + 12) % 12
    const chords = candidateChordsForPc(key, givenPc)

    let best = null
    for (const chord of chords) {
      const roleSchemes = rolesForMissingVoices(chord.givenRole)
      for (const roles of roleSchemes) {
        const combos = enumerateVoicings(missingVoices, roles, chord.tones, ranges, givenVoice, givenPitchStr, givenMidi)
        for (const combo of combos) {
          const { issues, motion } = scoreCandidate(key, timeSignature, theoryBase, prevPitches, combo.midis, combo.pitches)
          if (!best || issues < best.issues || (issues === best.issues && motion < best.motion)) {
            best = { issues, motion, candPitches: combo.pitches }
          }
        }
      }
    }

    if (!best) {
      warnings.push(`${i + 1}번째 박: 음역/성부간격 조건을 만족하는 보이싱을 찾지 못했습니다.`)
      for (const v of VOICE_NAMES) {
        filled[v].push(v === givenVoice ? { pitch: givenPitchStr, duration: 'q' } : { isRest: true, duration: 'q' })
      }
      prevPitches = null
      continue
    }
    if (best.issues > 0) {
      warnings.push(`${i + 1}번째 박: 성부진행 주의(${best.issues}건) — 더 나은 보이싱을 찾지 못했습니다.`)
    }
    for (const v of VOICE_NAMES) {
      filled[v].push({ pitch: best.candPitches[v], duration: 'q' })
    }
    prevPitches = best.candPitches
  }

  return {
    score: { ...score, ...Object.fromEntries(missingVoices.map((v) => [v, filled[v]])) },
    warnings,
  }
}

// ─── 문제 생성: 주어진 키·박자·마디수로 화성 흐름을 암시하는 랜덤 멜로디 생성 ──────────
// 도수(0~6) 간 전형적인 화성 진행 전이표 — 매 박마다 이 표를 따라 다음 화음을 무작위로 고른다.
const DEGREE_TRANSITIONS = {
  0: [3, 4, 5, 1, 2],
  1: [4, 6, 3],
  2: [5, 3],
  3: [4, 0, 1],
  4: [0, 5],
  5: [3, 1, 4],
  6: [0],
}

function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

/**
 * 주어진 성부(soprano|bass)의 랜덤 멜로디를 생성한다 — 화성 진행을 암시하도록
 * 매 박 온음계 3화음(도수 0~6)을 전형적인 진행표에 따라 무작위로 고르고,
 * 그 화음음(근/3/5음) 중 직전 음과 가까운 쪽을 우선(약간의 도약 허용)해서 선율을 만든다.
 * 마지막 박은 항상 으뜸화음(I)으로 종지한다.
 *
 * @param {object} opts
 * @param {string} opts.key
 * @param {string} opts.timeSignature
 * @param {number} opts.measureCount
 * @param {'soprano'|'bass'} opts.voice
 * @param {'baek'|'kostka'} [opts.theoryBase]
 * @param {string} [opts.duration] 중심 리듬 — 멜로디 음표 길이이자 화성 1회 유지 길이('w'|'h'|'q'|'8').
 * @param {string} [opts.solveLevel] 백병동 편·장 id (예: '1-3', '2-1').
 * @returns {Array<{pitch:string, duration:string}>}
 */
export function generateProblemMelody({
  key, timeSignature, measureCount, voice, theoryBase = 'baek', duration = 'q', solveLevel = '1-3',
}) {
  // solveLevel: generateHarmonyProgression에서 허용 화음군을 좁히는 데 사용.
  void solveLevel
  const beatsPerMeasure = getBeatsPerMeasure(timeSignature)
  const totalQuarterBeats = Math.max(1, measureCount || 1) * beatsPerMeasure
  const unitBeats = BEAT_VALUES[duration] ?? 1
  const totalSlots = Math.max(1, Math.round(totalQuarterBeats / unitBeats))
  const range = getVoiceRanges(theoryBase)[voice]

  const notes = []
  let degree = 0
  let prevMidi = null

  for (let i = 0; i < totalSlots; i++) {
    if (i === totalSlots - 1) {
      degree = 0 // 종지: 마지막 박은 항상 I
    } else if (i > 0) {
      degree = randomChoice(DEGREE_TRANSITIONS[degree] ?? [0])
    }

    const { tones } = degreeChordTones(key, degree)
    const options = ['root', 'third', 'fifth'].flatMap((role) => pitchOptionsForRole(tones[role].letterAcc, range))

    let chosen
    if (prevMidi == null) {
      const mid = (range.minMidi + range.maxMidi) / 2
      chosen = [...options].sort((a, b) => Math.abs(a.midi - mid) - Math.abs(b.midi - mid))[0]
    } else {
      const sorted = [...options].sort((a, b) => Math.abs(a.midi - prevMidi) - Math.abs(b.midi - prevMidi))
      // 70%는 가장 가까운 음(순차 진행 선호), 30%는 상위 3개 중 무작위(약간의 도약 허용)
      chosen = Math.random() < 0.7 ? sorted[0] : sorted[Math.floor(Math.random() * Math.min(3, sorted.length))]
    }
    notes.push({ pitch: chosen.pitch, duration })
    prevMidi = chosen.midi
  }

  return notes
}
